import { QueryClient } from 'react-query';
import { omit } from 'lodash';

import { Application, Stage } from '~/types/Application';
import { isAgent } from '~/utils/getDecodedJwt';
import { getStagePayload } from '~/utils/getStagePayload';

const REVIEW_PAYLOAD_KEYS: Record<string, string> = {
  'review.companyInformation': 'reviewCompanyInformation',
  'review.managers': 'reviewManagers',
  'review.shareholders': 'reviewShareholders',
};

interface AgentReviewEdits {
  applicationId: string;
  stageKey: Stage;
  stepKey: string;
  payloadKey: string;
  fields: Set<string>;
  draftWrites: Set<Promise<unknown>>;
  submitting: boolean;
  submitted: boolean;
}

const editsByClient = new WeakMap<QueryClient, Map<string, AgentReviewEdits>>();

export const isManuallyAuditedStep = (stepKey: string) => !!REVIEW_PAYLOAD_KEYS[stepKey];

export const getAgentReviewEdits = (
  client: QueryClient, application: Application | undefined, stepKey?: string,
): AgentReviewEdits | undefined => {
  const activeStep = stepKey ?? getStagePayload.wizard(application)?.activeStep?.key;
  const payloadKey = activeStep && REVIEW_PAYLOAD_KEYS[activeStep];
  if (!isAgent() || !application?.uid || !activeStep || !payloadKey ||
    (application.stage !== Stage.ksaReview && application.stage !== Stage.ksaContract)) {
    return undefined;
  }
  let scopes = editsByClient.get(client);
  if (!scopes) {
    scopes = new Map();
    editsByClient.set(client, scopes);
  }
  const scope = `${application.uid}:${application.stage}:${activeStep}`;
  let edits = scopes.get(scope);
  if (!edits) {
    edits = {
      applicationId: application.uid,
      stageKey: application.stage,
      stepKey: activeStep,
      payloadKey,
      fields: new Set(),
      draftWrites: new Set(),
      submitting: false,
      submitted: false,
    };
    scopes.set(scope, edits);
  }
  return edits;
};

export const recordAgentReviewEdit = (edits: AgentReviewEdits, path: string) => {
  if (path.startsWith(`${edits.payloadKey}.`) ||
    (edits.payloadKey === 'reviewCompanyInformation' && path.startsWith('companyInformation.'))) {
    Object.assign(edits, { submitted: false });
    edits.fields.add(path);
  }
};

export const restoreAgentReviewEdits = (edits: AgentReviewEdits | undefined, draft: any) => {
  if (edits && !edits.submitting && !edits.submitted && Array.isArray(draft?.agentEditedFields)) {
    draft.agentEditedFields.forEach((path: unknown) => {
      if (typeof path === 'string') recordAgentReviewEdit(edits, path);
    });
  }
};

export const withoutAgentEditMetadata = <T,>(draft: T): T => {
  if (!draft || typeof draft !== 'object' || Array.isArray(draft)) return draft;
  return omit(draft, 'agentEditedFields') as T;
};
